import { useFlag } from '@calendly/feature-check-react'
import { useQueryParams } from '@calendly/hooks'
import { useLocation, useRouteMatch } from 'react-router-dom'

import { historyPush } from 'actions/core/navigation'
import { MEETINGS_PRESET_IDS, MEETINGS_PRESET_PARAM } from 'constants/meetings'
import { useProductRefreshAppShell } from 'features/app_shell/product_refresh/useProductRefreshAppShell'
import { getWorkflowsAppBasePath } from 'services/workflows/url'

import { MEETINGS_TAB_BASE_PATH_LEGACY,
  MEETINGS_TAB_BASE_PATH_REBRAND } from './meetings_tab_paths'

/** List URL base when routing forms live under Scheduling (product refresh shell). */
export const SCHEDULING_ROUTING_FORMS_TAB_BASE = '/app/scheduling/routing/forms'

const ROUTING_FORMS_TAB_BASE_LEGACY = '/app/routing/forms'

const TAB_PATH_BY_NAME = {
  event_types: '/event_types',
  scheduling: '/app/scheduling',
  scheduling_meeting_types: '/app/scheduling/meeting_types',
  scheduling_single_use_links: '/app/scheduling/single_use_links',
  scheduling_polls: '/app/scheduling/polls',
  scheduling_callie: '/app/scheduling/callie',
  scheduling_availability: '/app/scheduling/availability',
  scheduled_events: '/app/scheduled_events',
  landing_pages: '/app/landing_pages',
  routing: ROUTING_FORMS_TAB_BASE_LEGACY,
  contacts: '/app/contacts',
  meetings: MEETINGS_TAB_BASE_PATH_LEGACY,
  recaps: '/app/recaps',
  today: '/app/today'
}

const TAB_NAME_BY_PATH = {
  '/event_types': 'event_types',
  '/app/scheduling/meeting_types': 'scheduling',
  '/app/scheduling/single_use_links': 'scheduling',
  '/app/scheduling/polls': 'scheduling',
  '/app/scheduling/callie': 'scheduling',
  '/app/scheduling/availability': 'scheduling',
  '/app/scheduled_events': 'scheduled_events',
  '/app/workflows': 'workflows',
  '/app/automations': 'workflows',
  '/app/landing_pages': 'landing_pages',
  [ROUTING_FORMS_TAB_BASE_LEGACY]: 'routing',
  [SCHEDULING_ROUTING_FORMS_TAB_BASE]: 'scheduling',
  '/app/contacts': 'contacts',
  [MEETINGS_TAB_BASE_PATH_LEGACY]: 'meetings',
  [MEETINGS_TAB_BASE_PATH_REBRAND]: 'meetings',
  '/app/recaps': 'recaps',
  '/app/today': 'today'
}

const tabPathValuesForHomeRoute = (isProductRefreshAppShell) => Object.entries(TAB_PATH_BY_NAME).flatMap(([key, p]) => {
  if (key === 'meetings') {
    return [MEETINGS_TAB_BASE_PATH_LEGACY, MEETINGS_TAB_BASE_PATH_REBRAND]
  }
  if (key === 'routing') {
    return isProductRefreshAppShell
      ? [SCHEDULING_ROUTING_FORMS_TAB_BASE]
      : [ROUTING_FORMS_TAB_BASE_LEGACY, SCHEDULING_ROUTING_FORMS_TAB_BASE]
  }
  return [p]
})

/** Path pattern for the Home `Route` and `useRouteMatch` — must stay in sync. */
export const getHomePaths = (isProductRefreshAppShell) => [
  `(${[...tabPathValuesForHomeRoute(isProductRefreshAppShell), '/app/workflows', '/app/automations'].join('|')})`,
  '(user|team|user_group|all_users|all_users_and_teams)',
  ':id?'
].join('/')

/** True if pathname is under the meetings/calendar app tab (either legacy or rebranded prefix). */
export const isMeetingsTabPathPrefix = (pathname) => (
  pathname.startsWith(`${MEETINGS_TAB_BASE_PATH_LEGACY}/`)
  || pathname === MEETINGS_TAB_BASE_PATH_LEGACY
  || pathname.startsWith(`${MEETINGS_TAB_BASE_PATH_REBRAND}/`)
  || pathname === MEETINGS_TAB_BASE_PATH_REBRAND
)

const getTabPath = (params) => params[0]

const getScopeType = (params) => params[1] || 'user'

const getScopeId = (params) => (params[1] ? params.id : 'me')

const getScopePath = (params) => {
  const type = getScopeType(params)
  const id = getScopeId(params)

  return id ? `${type}/${id}` : type
}

const DATE_PRESET_PARAM_VALUES = new Set([
  MEETINGS_PRESET_IDS.TODAY,
  MEETINGS_PRESET_IDS.THIS_WEEK,
  MEETINGS_PRESET_IDS.LAST_WEEK,
])

/** Query string for meetings tab scope changes — wipes filters; preset mode may keep a date preset. */
const buildMeetingsScopeChangeQuery = (queryParams, presetFlagOn) => {
  if (!presetFlagOn) return ''

  const presetParam = queryParams[MEETINGS_PRESET_PARAM]
  if (presetParam && DATE_PRESET_PARAM_VALUES.has(presetParam)) {
    return `?${new URLSearchParams({ [MEETINGS_PRESET_PARAM]: presetParam }).toString()}`
  }

  return ''
}

const getScopeTypeV1 = (params) => {
  const type = getScopeType(params)
  const id = getScopeId(params)

  switch (type) {
    case 'user': {
      if (id === 'me') {
        return 'my_calendly'
      } if (id) {
        return 'user_single'
      }
      return 'all_users'
    }
    case 'team': {
      if (id) {
        return 'page_single'
      }
      return 'page'
    }
    case 'all_users_and_teams':
      return 'all_users_and_teams'
    case 'all_users':
      return 'all_users'
    case 'user_group':
      return 'user_group'
    default:
      return null
  }
}

const getScopeTypeV2 = (params) => {
  const type = getScopeType(params)
  const id = getScopeId(params)

  switch (type) {
    case 'all_users_and_teams':
      return 'all_users_and_teams'
    case 'all_users':
      return 'users'
    case 'user_group': {
      return 'user_group'
    }
    case 'user': {
      if (id) {
        return 'user'
      }
      return 'users'
    }
    case 'team': {
      if (id) {
        return 'team'
      }
      return 'teams'
    }
    default:
      return null
  }
}


const scopeV1ToPath = ({ type, id }) => {
  switch (type) {
    case 'my_calendly':
      return 'user/me'
    case 'all_users_and_teams':
      return 'all_users_and_teams'
    case 'all_users':
      return 'all_users'
    case 'page':
      return 'team'
    case 'user_single':
      return `user/${id}`
    case 'page_single':
      return `team/${id}`
    case 'user_group':
      return `user_group/${id}`
    default:
      return null
  }
}

/*
 * TODO: Get rid of scope versions.
 */

export const useHomeTabs = () => {
  const isProductRefreshAppShell = useProductRefreshAppShell()
  const presetFlagOn = useFlag('sa_default_preset_toggle', false)
  const meetingsTabBasePath = isProductRefreshAppShell ? MEETINGS_TAB_BASE_PATH_REBRAND : MEETINGS_TAB_BASE_PATH_LEGACY
  const routingTabBasePath = isProductRefreshAppShell
    ? SCHEDULING_ROUTING_FORMS_TAB_BASE
    : ROUTING_FORMS_TAB_BASE_LEGACY

  const { pathname } = useLocation()
  const homePathsForMatch = getHomePaths(isProductRefreshAppShell)
  const { params = {} } = useRouteMatch({ path: homePathsForMatch }) || {}
  const queryParams = useQueryParams()
  const isWorkflowsAppShellRebrand = isProductRefreshAppShell

  const tabNameToPath = (name) => {
    if (name === 'meetings') return meetingsTabBasePath
    if (name === 'routing') return routingTabBasePath
    if (name === 'workflows') return getWorkflowsAppBasePath(isWorkflowsAppShellRebrand)
    return TAB_PATH_BY_NAME[name]
  }

  const getTabName = () => {
    if (isProductRefreshAppShell && pathname.startsWith('/app/routing/forms')) {
      return 'scheduling'
    }
    const fromParams = TAB_NAME_BY_PATH[getTabPath(params)]
    if (fromParams) return fromParams
    if (pathname === '/app/contacts/opportunities') {
      return 'contacts'
    }
    if (isProductRefreshAppShell && pathname.startsWith('/app/scheduling/routing')) {
      return 'scheduling'
    }
    return undefined
  }

  const getTabScopeV1 = () => {
    const type = getScopeTypeV1(params)
    const id = getScopeId(params)
    return { type, id }
  }

  const getTabScopeV2 = () => {
    const type = getScopeTypeV2(params)
    const id = getScopeId(params)
    return { type, id }
  }

  const getTabPathByName = (name) => {
    const tabPath = tabNameToPath(name)
    const scopePath = getScopePath(params)

    return `${tabPath}/${scopePath}`
  }

  const getMeetingsTabBasePath = () => meetingsTabBasePath

  const setTabScopeV1 = (scope) => (dispatch) => {
    let tabPath = getTabPath(params)
    if (tabPath === MEETINGS_TAB_BASE_PATH_LEGACY || tabPath === MEETINGS_TAB_BASE_PATH_REBRAND) {
      tabPath = meetingsTabBasePath
    }
    if (tabPath === ROUTING_FORMS_TAB_BASE_LEGACY || tabPath === SCHEDULING_ROUTING_FORMS_TAB_BASE) {
      tabPath = routingTabBasePath
    }
    const scopePath = scopeV1ToPath(scope)
    let path = `${tabPath}/${scopePath}`
    if (getTabName() === 'meetings') {
      // Wipe all filters on scope change. Legacy mode clears everything; preset mode keeps only an
      // active date preset chip (today / this week / last week) or defaults back to Upcoming.
      path += buildMeetingsScopeChangeQuery(queryParams, presetFlagOn)
    }
    dispatch(historyPush(path))
  }

  return {
    getTabName,
    getTabPathByName,
    getMeetingsTabBasePath,
    getTabScopeV1,
    setTabScopeV1,
    getTabScopeV2
  }
}
